import { Injectable } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin } from 'rxjs';
import { ISeoTagType } from '@app/shared/services/seo/seo-tag-type.enum';
import { ISeoTag } from '@app/shared/services/seo/seo-tag.interface';
import { cloneDeep } from 'lodash-es';

@Injectable({
  providedIn: 'root'
})
export class SEOService {

  private currentSeoTags: ISeoTag[] = [];
  private readonly allDefaultSeoTags: ISeoTag[] =
    [
      {
        tagType: ISeoTagType.Title,
        i18nKey: 'SHARED.DEFAULT-META.META-TITLE'
      },
      {
        tagType: ISeoTagType.Description,
        i18nKey: 'SHARED.DEFAULT-META.META-DESCRIPTION'
      },
      {
        tagType: ISeoTagType.Keywords,
        i18nKey: 'SHARED.DEFAULT-META.META-KEYWORDS'
      }
    ];

  constructor(
    private title: Title,
    private meta: Meta,
    private translate: TranslateService) {
    this.setSEO(this.allDefaultSeoTags);
    this.listenToLanguageChange();
  }

  setSEO(seoTags: ISeoTag[]) {
    this.prepCurrentSeoTags(seoTags);
    const metaTitle = this.currentSeoTags[0];
    const metaDescription = this.currentSeoTags[1];
    const metaKeywords = this.currentSeoTags[2];
    forkJoin([
      this.translate.get(metaTitle.i18nKey, metaTitle.i18nParams ? metaTitle.i18nParams : null),
      this.translate.get(metaDescription.i18nKey, metaDescription.i18nParams ? metaDescription.i18nParams : null),
      this.translate.get(metaKeywords.i18nKey, metaKeywords.i18nParams ? metaKeywords.i18nParams : null)])
      .subscribe(([title, description, keywords]) => {
        this.updateTitle(title);
        this.updateDescription(description);
        this.updateKeywords(keywords);
      });
  }

  private prepCurrentSeoTags(seoTags: ISeoTag[]) {
    this.currentSeoTags = cloneDeep(this.allDefaultSeoTags);
    seoTags.forEach(seoTag => {
      const tag = this.currentSeoTags.find(currentSeoTag => currentSeoTag.tagType === seoTag.tagType);
      tag.i18nKey = seoTag.i18nKey;
      if (seoTag.i18nParams) {
        tag.i18nParams = seoTag.i18nParams;
      }
    });
  }

  private listenToLanguageChange() {
    this.translate.onLangChange
      .subscribe(() => {
        this.setSEO(this.currentSeoTags);
      });
  }

  private updateTitle(title: string) {
    this.title.setTitle(title);
  }

  private updateDescription(desc: string) {
    this.meta.updateTag({ name: 'description', content: desc });
  }

  private updateKeywords(keywords: string) {
    this.meta.updateTag({ name: 'keywords', content: keywords });
  }

}
